/**
 * The sign-in page's background (17 Oct 2026): a dark field of code with dubizzle flames and wordmarks set into it.
 * Where the pointer goes, code and logos heat up from dim grey through brand red to a hot orange, leaving a glowing
 * trail that cools. With no pointer (a phone, or a mouse left still) a slow "ghost" drifts across so the page never
 * looks frozen. One canvas: the cold layer is drawn once to an offscreen canvas, and each frame only redraws what is
 * warm, so it costs a couple of milliseconds. Paused while the tab is hidden; still for people who ask for less motion.
 */
import { useEffect, useRef } from 'react';
import { FLAME, WORDMARK } from '../taskforce/logo';

const SNIPPETS = [
  'const live = await deploy(task);',
  "if (task.status === 'Live') celebrate();",
  'git push origin main',
  '<TaskForce />',
  'npm run build && npm test',
  "SELECT * FROM tasks WHERE status = 'live';",
  'export default function dubizzle() {',
  'return <Flame size={42} />;',
  'for (const t of sprint) t.ship();',
  '// ship fast, learn faster',
  'await review(pullRequest);',
  "owner.notify('ready for QA');",
  'while (!done) iterate();',
  'const team = new Squad({ focus: true });',
  'app.listen(3000);',
  'deploy --env=production',
  "type Status = 'Backlog' | 'In Dev' | 'Live';",
  'useEffect(() => sync(), [tasks]);',
  'curl -s /healthz  # {"ok":true}',
  'merge(main, feature);',
];
const GLYPHS = '{}[]()<>/=;:+*&|!?#$%01';

// Cold, warm and hot colours (RGB).
const COLD: [number, number, number] = [255, 255, 255];
const RED: [number, number, number] = [224, 0, 0];
const HOT: [number, number, number] = [255, 170, 40];

function heatColor(h: number): string {
  // 0 → dim white; 0.5 → brand red; 1 → hot orange.
  const mix = (a: number[], b: number[], t: number) => a.map((v, i) => Math.round(v + (b[i]! - v) * t));
  const [r, g, b] = h < 0.5 ? mix(COLD, RED, h * 2) : mix(RED, HOT, (h - 0.5) * 2);
  const alpha = 0.09 + h * 0.91;
  return `rgba(${r},${g},${b},${alpha.toFixed(3)})`;
}

interface Logo {
  kind: 'flame' | 'word';
  x: number;
  y: number;
  w: number;
  h: number;
}

export function CodeField() {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const flameRed = new Path2D(FLAME.red);
    const flameInk = new Path2D(FLAME.ink);
    const wordRed = new Path2D(WORDMARK.red);
    const wordInk = new Path2D(WORDMARK.ink);
    const [fx, fy, fw, fh] = FLAME.viewBox.split(' ').map(Number) as [number, number, number, number];
    const [wx, wy, ww, wh] = WORDMARK.viewBox.split(' ').map(Number) as [number, number, number, number];

    let W = 0;
    let H = 0;
    // Rows are `cell` tall; characters are narrower (cw), as monospace code is.
    let cell = 22;
    let cw = 13;
    let cols = 0;
    let rows = 0;
    let chars: string[] = [];
    let heat = new Float32Array(0);
    let logos: Logo[] = [];
    const base = document.createElement('canvas');
    const bctx = base.getContext('2d')!;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const font = () => `500 ${Math.round(cell * 0.62)}px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace`;

    const drawLogo = (c: CanvasRenderingContext2D, l: Logo, red: string, ink: string) => {
      c.save();
      if (l.kind === 'flame') {
        c.translate(l.x, l.y);
        c.scale(l.w / fw, l.h / fh);
        c.translate(-fx, -fy);
        c.fillStyle = red;
        c.fill(flameRed, 'evenodd');
        c.fillStyle = ink;
        c.fill(flameInk, 'evenodd');
      } else {
        c.translate(l.x, l.y);
        c.scale(l.w / ww, l.h / wh);
        c.translate(-wx, -wy);
        c.fillStyle = red;
        c.fill(wordRed, 'evenodd');
        c.fillStyle = ink;
        c.fill(wordInk, 'evenodd');
      }
      c.restore();
    };
    const drawCell = (c: CanvasRenderingContext2D, i: number, color: string) => {
      const ch = chars[i]!;
      if (ch === ' ') return;
      c.fillStyle = color;
      c.fillText(ch, (i % cols) * cw + cw / 2, Math.floor(i / cols) * cell + cell / 2);
    };
    const paintBase = () => {
      bctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      bctx.clearRect(0, 0, W, H);
      bctx.font = font();
      bctx.textAlign = 'center';
      bctx.textBaseline = 'middle';
      for (let i = 0; i < chars.length; i++) drawCell(bctx, i, heatColor(0));
      for (const l of logos) drawLogo(bctx, l, 'rgba(255,255,255,0.07)', 'rgba(255,255,255,0.05)');
    };
    const repaintCell = (i: number) => {
      const x = (i % cols) * cw;
      const y = Math.floor(i / cols) * cell;
      bctx.clearRect(x, y, cw, cell);
      drawCell(bctx, i, heatColor(0));
    };

    const layout = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      cell = W < 700 ? 17 : 22;
      cw = Math.round(cell * 0.6);
      cols = Math.ceil(W / cw);
      rows = Math.ceil(H / cell);
      for (const c of [canvas, base]) {
        c.width = W * dpr;
        c.height = H * dpr;
      }
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      // Rows of code: snippets one after another, each row starting somewhere different.
      chars = [];
      for (let r = 0; r < rows; r++) {
        let line = ' '.repeat(Math.floor(Math.random() * 6));
        while (line.length < cols) line += SNIPPETS[Math.floor(Math.random() * SNIPPETS.length)] + '   ';
        chars.push(...line.slice(0, cols).split(''));
      }
      heat = new Float32Array(cols * rows);
      // Logos set into the code, on a coarse grid so they never overlap; their cells are cleared.
      logos = [];
      const step = cell * (W < 700 ? 7 : 9);
      for (let gy = step * 0.4; gy < H - step * 0.6; gy += step) {
        for (let gx = step * 0.3; gx < W - step * 0.6; gx += step) {
          const roll = Math.random();
          if (roll > 0.22) continue;
          const word = roll < 0.05 && gx < W - step * 2.4;
          const h = word ? cell * 2.6 : cell * (2.2 + Math.random() * 1.6);
          const w = word ? (h * ww) / wh : (h * fw) / fh;
          const l: Logo = { kind: word ? 'word' : 'flame', x: gx + Math.random() * cell, y: gy, w, h };
          logos.push(l);
          for (let y = Math.floor(l.y / cell); y <= Math.floor((l.y + l.h) / cell); y++)
            for (let x = Math.floor(l.x / cw) - 1; x <= Math.floor((l.x + l.w) / cw) + 1; x++)
              if (x >= 0 && y >= 0 && x < cols && y < rows) chars[y * cols + x] = ' ';
          if (word) gx += step * 1.4;
        }
      }
      paintBase();
    };

    // The pointer, smoothed, and the ghost that drifts when nobody is moving one.
    const p = { x: -1e4, y: -1e4, tx: -1e4, ty: -1e4, last: 0 };
    const move = (x: number, y: number) => {
      p.tx = x;
      p.ty = y;
      p.last = performance.now();
    };
    const onPointer = (e: PointerEvent) => move(e.clientX, e.clientY);
    const onLeave = () => (p.last = 0);

    let raf = 0;
    let glitchAt = 0;
    const restore: Array<[number, string, number]> = [];
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      // No pointer for two seconds: the ghost takes over.
      if (now - p.last > 2000) {
        const t = now / 1000;
        p.tx = W * (0.5 + 0.38 * Math.sin(t * 0.23));
        p.ty = H * (0.5 + 0.34 * Math.sin(t * 0.37 + 1.3));
      }
      if (p.x < -1e3) {
        p.x = p.tx;
        p.y = p.ty;
      }
      p.x += (p.tx - p.x) * 0.18;
      p.y += (p.ty - p.y) * 0.18;
      // A few characters flicker to other glyphs and back: code being typed.
      if (now > glitchAt) {
        glitchAt = now + 90;
        for (let k = 0; k < 3; k++) {
          const i = Math.floor(Math.random() * chars.length);
          if (chars[i] === ' ') continue;
          restore.push([i, chars[i]!, now + 500 + Math.random() * 700]);
          chars[i] = GLYPHS[Math.floor(Math.random() * GLYPHS.length)]!;
          repaintCell(i);
        }
      }
      for (let k = restore.length - 1; k >= 0; k--) {
        const [i, ch, at] = restore[k]!;
        if (now < at) continue;
        chars[i] = ch;
        repaintCell(i);
        restore.splice(k, 1);
      }
      // Heat: a soft circle around the pointer, cooling everywhere else.
      const R = Math.min(200, Math.max(120, W * 0.12));
      const pc = Math.floor(p.x / cw);
      const pr = Math.floor(p.y / cell);
      const span = Math.ceil(R / cell);
      const spanX = Math.ceil(R / cw);
      for (let i = 0; i < heat.length; i++) if (heat[i]! > 0.003) heat[i]! *= 0.93;
      for (let r = pr - span; r <= pr + span; r++) {
        if (r < 0 || r >= rows) continue;
        for (let c = pc - spanX; c <= pc + spanX; c++) {
          if (c < 0 || c >= cols) continue;
          const dx = c * cw + cw / 2 - p.x;
          const dy = r * cell + cell / 2 - p.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d >= R) continue;
          const t = 1 - d / R;
          const v = t * t * (3 - 2 * t);
          const i = r * cols + c;
          if (v > heat[i]!) heat[i] = v;
        }
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      ctx.drawImage(base, 0, 0, W, H);
      ctx.font = font();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      for (let i = 0; i < heat.length; i++) {
        const h = heat[i]!;
        if (h < 0.02 || chars[i] === ' ') continue;
        drawCell(ctx, i, heatColor(h));
      }
      for (const l of logos) {
        const c = Math.min(cols - 1, Math.max(0, Math.floor((l.x + l.w / 2) / cw)));
        const r = Math.min(rows - 1, Math.max(0, Math.floor((l.y + l.h / 2) / cell)));
        const h = heat[r * cols + c]!;
        if (h < 0.02) continue;
        drawLogo(ctx, l, heatColor(Math.min(1, 0.45 + h * 0.55)), `rgba(255,255,255,${(0.08 + h * 0.8).toFixed(3)})`);
      }
      // The pointer's own glow.
      const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, R * 1.3);
      g.addColorStop(0, 'rgba(255,90,31,0.10)');
      g.addColorStop(1, 'rgba(255,90,31,0)');
      ctx.fillStyle = g;
      ctx.fillRect(p.x - R * 1.3, p.y - R * 1.3, R * 2.6, R * 2.6);
    };

    let resizeTimer: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        layout();
        if (still) ctx.drawImage(base, 0, 0, W, H);
      }, 150);
    };
    const onVisibility = () => {
      cancelAnimationFrame(raf);
      if (!document.hidden && !still) raf = requestAnimationFrame(frame);
    };
    layout();
    if (still) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.drawImage(base, 0, 0, W, H);
    } else {
      raf = requestAnimationFrame(frame);
      window.addEventListener('pointermove', onPointer, { passive: true });
      window.addEventListener('pointerdown', onPointer, { passive: true });
      document.addEventListener('pointerleave', onLeave);
      document.addEventListener('visibilitychange', onVisibility);
    }
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(resizeTimer);
      window.removeEventListener('pointermove', onPointer);
      window.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('pointerleave', onLeave);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('resize', onResize);
    };
  }, []);
  return <canvas ref={ref} className="codefield" aria-hidden="true" />;
}
